// @ts-nocheck
import AuthLayout from './layouts/AuthLayout/AuthLayout';
import RootLayout from './layouts/RootLayout';
import FinalBookingPayment from './pages/FinalBookingPayment/FinalBookingPayment';
import { getManageBookingById } from './services/booking/booking';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ForgotPasswordSucess from './pages/ForgotPasswordSucess/ForgotPasswordSuccess';
import Login from './pages/Login/Login';
import SendPassword from './pages/SendPassword/SendPassword';
import SetNewPassword from './pages/SetNewPassword/SetNewPassword';
import ShellSkeleton from './components/Skeleton/ShellSkeleton/ShellSkeleton';
import ForgotPasswordError from './pages/ForgotPasswordError/ForgotPasswordError';
import Trip from './pages/Trip/Trip';
import Trips from './pages/Trips/Trips';
import Itineraries from './pages/Itineraries/Itineraries';
import NotFoundPage from './components/NotFoundPage/NotFoundPage';
import SuccessfulPaymentMessage from './components/Payment/SuccessfulPaymentMessage/SuccessfulPaymentMessage';
import GlobalSettings from './pages/GlobalSettings/GlobalSettings';
import BookingModalPage from './pages/BookingModal/BookingModalPage';
import Library from './pages/Library/Library';
import TravelDoc from './components/Trip/DetailedItinerary/TravelDoc/TravelDoc';
import TravelerApp from './pwa/TravelerApp/TravelerApp';
import OnlineStatusProvider from './pwa/TravelerApp/context/OnlineStatusContext/OnlineStatusContext';
import FeatureFlagTest from './components/Test/FeatureFlagTest/FeatureFlagTest';
import PassengerFieldsSecondRound from './pages/PassengerFieldsSecondRound/PassengerFieldsSecondRound';
import { createBrowserRouter, redirect } from 'react-router-dom';
import { Suspense } from 'react';
import { validateUserSession } from './services/auth/auth';
import { getTenant } from './services/tenant/tenant';
import Reports from './pages/Reports/Reports';

// Function to refresh tenant data and store it in localStorage
const refreshTenantData = async (request) => {
  try {
    //Skip tenant data refresh for trips listing with search params
    if (request?.request?.url?.indexOf('trips?') != -1) {
      return { success: true };
    }

    const response = await getTenant();
    if (response.data) {
      const { tenantdomain, tenantname, tenantlogo } = response.data;

      const tenantData = {
        domain: tenantdomain,
        name: tenantname,
        logo: tenantlogo,
        ...(response?.data || {})
      };

      localStorage.setItem('tenantData', JSON.stringify(tenantData));

      return { success: true };
    }
  } catch (error) {
    // Don't block navigation if tenant refresh fails
    return { success: true };
  }
};

// Enhanced protected route with session validation
const protectedRouteWithValidation = async (request) => {
  try {
    const sessionResult = await validateUserSession(false);
    if (!sessionResult.isValid) {
      return redirect('/login?sessionExpired=true');
    }

    // Refresh tenant data on successful session validation
    await refreshTenantData(request);

    return null;
  } catch (error) {
    console.error('Session validation error:', error);
    return redirect('/login?sessionExpired=true');
  }
};

const rootRedirectWithSessionCheck = async (request) => {
  try {
    const sessionResult = await validateUserSession(true);

    if (sessionResult.isValid) {
      // Refresh tenant data before redirecting to trips
      await refreshTenantData(request);
      return redirect('/trips');
    }

    // If there is no session at all, go directly to login without “expired” messaging.
    if (sessionResult?.error === 'No local session found') {
      return redirect('/login');
    }

    return redirect('/login?sessionExpired=true');
  } catch (error) {
    console.error('Session validation error:', error);
    return redirect('/login?sessionExpired=true');
  }
};

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        loader: rootRedirectWithSessionCheck,
        element: null
      },
      {
        path: '/trips',
        element: (
          <Suspense fallback={<ShellSkeleton id='trips-page' />}>
            <Trips id='trips-page' />
          </Suspense>
        ),
        loader: protectedRouteWithValidation
      },
      {
        path: '/trips/:id',
        element: (
          <Suspense fallback={<ShellSkeleton id='trips-page' />}>
            <Trip id='trip-page' />
          </Suspense>
        ),
        loader: async (request = {}) => {
          const { id: tripId } = request?.params || {};

          // Validate session first
          const sessionResult = await validateUserSession(false);
          if (!sessionResult.isValid) {
            return redirect('/login?sessionExpired=true');
          }

          await refreshTenantData(request);

          return { tripId };
        }
      },
      {
        path: '/itineraries/:id',
        element: <Itineraries id='output-page' />,
        loader: async (request) => {
          const { id: tripId } = request?.params || {};

          await refreshTenantData(request);

          return { tripId };
        }
      },
      {
        path: '/itineraries/:id/travel-doc',
        element: <TravelDoc />,
        loader: ({ params }) => {
          const { id: tripId } = params;

          return { tripId };
        }
      },
      {
        path: '/global-settings',
        element: (
          <Suspense fallback={<ShellSkeleton id='global-settings-skeleton' />}>
            <GlobalSettings id='global-settings-page' />
          </Suspense>
        ),
        loader: protectedRouteWithValidation
      },

      {
        path: '/reports',
        element: (
          <Suspense fallback={<ShellSkeleton id='global-settings-page' />}>
            <Reports id='global-settings-page' />
          </Suspense>
        ),
        loader: protectedRouteWithValidation
      },
      {
        path: '/bookings/:id',
        loader: async (request) => {
          const { id: bookingId } = request?.params || {};

          const sessionResult = await validateUserSession(false);
          if (!sessionResult.isValid) {
            return redirect('/login?sessionExpired=true');
          }

          await refreshTenantData(request);

          try {
            const { data } = await getManageBookingById(bookingId);
            if (data?.tripid) {
              return redirect(
                `/trips/${data.tripid}?managebooking=${bookingId}`
              );
            }
          } catch {
            // fall through
          }

          return redirect('/trips');
        }
      },
      {
        path: '/bookings/:id/payment',
        element: <FinalBookingPayment id='final-booking-payment' />,
        loader: async (request) => {
          const { id: bookingId } = request?.params || {};

          await refreshTenantData(request);

          return { bookingId };
        }
      },
      {
        path: '/bookings/:id/payment/success',
        element: <SuccessfulPaymentMessage id='successful-payment' />,
        loader: ({ params }) => {
          const { id: bookingId } = params;

          return { bookingId };
        }
      },
      {
        path: '/login',
        element: (
          <AuthLayout id='auth-layout'>
            <Login id='login-page' />
          </AuthLayout>
        ),
        loader: refreshTenantData
      },
      {
        path: '/forgot-password',
        element: (
          <AuthLayout id='auth-layout'>
            <ForgotPassword id='forgot-password-page' />
          </AuthLayout>
        )
      },
      {
        path: '/forgot-password/success',
        element: (
          <AuthLayout id='auth-layout'>
            <ForgotPasswordSucess id='forgot-password-success-page' />
          </AuthLayout>
        )
      },
      {
        path: '/forgot-password/error',
        element: (
          <AuthLayout id='auth-layout'>
            <ForgotPasswordError id='forgot-password-error-page' />
          </AuthLayout>
        )
      },
      {
        path: '/forgot-password/send',
        element: (
          <AuthLayout id='auth-layout'>
            <SendPassword id='send-password-page' />
          </AuthLayout>
        )
      },
      {
        path: '/forgot-password/new/:resetToken',
        element: (
          <AuthLayout id='auth-layout'>
            <SetNewPassword id='set-new-password-page' />
          </AuthLayout>
        )
      },
      {
        path: '/app/:tripId',
        element: (
          <OnlineStatusProvider>
            <TravelerApp />
          </OnlineStatusProvider>
        ),
        loader: ({ params }) => {
          const { tripId } = params;
          return { tripId };
        }
      },
      {
        path: '*',
        element: (
          <AuthLayout id='auth-layout'>
            <NotFoundPage id='not-found-page' />
          </AuthLayout>
        )
      },
      {
        path: '/shared/trip/:tripid/booking',
        element: <BookingModalPage />,
        loader: refreshTenantData
      },
      {
        path: '/library',
        element: (
          <Suspense fallback={<ShellSkeleton id='library-page' />}>
            <Library id='library-page' />
          </Suspense>
        ),
        loader: protectedRouteWithValidation
      },
      {
        path: '/test/feature-flags',
        element: <FeatureFlagTest />,
        loader: refreshTenantData
      },
      {
        path: '/passenger-form/:passengerKey',
        element: (
          <PassengerFieldsSecondRound id='passenger-fields-second-round-page' />
        ),
        loader: async (request) => {
          const { passengerKey } = request?.params || {};

          await refreshTenantData(request);

          return { passengerKey };
        }
      }
    ]
  }
]);
